import {isUndefined, without} from 'lodash';
import React, {useEffect, useReducer, useRef, useState} from 'react';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {computeIsGlobalNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {ResourceMap} from '../../../../libs/shared/core/src/store/commonAppTypes';
import {BaseFilterDropdownProps} from '../../../components/common/filters/common/types';
import {ModalFilterDropdown} from '../../../components/common/filters/modalFilter/modalFilterDropdown';
import {ModalFilterItemProps} from '../../../components/common/filters/modalFilter/modalFilterItem';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {AnalyticsFilterTypesEnum} from '../../../models/api/analyticsSavedViewModel';
import {TagModel} from '../../../models/api/tagModel';
import {updateFiltersAction} from '../../../store/panels/analytics/filters/analyticsPanelFiltersActions';
import {
  getAnalyticsExcludeTagFilters,
  getSelectedTeamNamespaces,
} from '../../../store/panels/analytics/filters/analyticsPanelFiltersSelectors';
import {getAnalyticsSelectedView} from '../../../store/panels/analytics/views/analyticsPanelViewsSelectors';
import {AnalyticsFilterButtonValue, FilterButtonSelectionLabels} from './common/analyticsFilterButtonValue';
import {analyticsFiltersConfig} from './common/analyticsFiltersConfig';
import {renderFilterTitle, renderTagButtonItem, useFiltersTracking} from './common/helpers';
import {TagsSelectionDropdown} from './common/tagSelectionDropdown';

/*
 * Types.
 */

enum ExcludeTagsFilterActionTypesEnum {
  DESELECT_TAG = 'deselect-tag',
  SET_EXCLUDED_TAGS = 'set-excluded-tags',
  SELECT_WITHOUT_TAGS = 'select-without-tags',
  CLEAR = 'clear',
  UPDATE_SELECTED_VALUE = 'update-selected-value',
}

interface ExcludeTagFiltersPanelState {
  [AnalyticsFilterTypesEnum.EXCLUDE_TAGS]?: ReadonlyArray<number>;
  [AnalyticsFilterTypesEnum.WITHOUT_TAGS]: boolean;
}

type State =
  | {
      excludedTags: ReadonlyArray<number>;
      withoutTags: false;
    }
  | {
      excludedTags: undefined;
      withoutTags: true;
    };

type Action =
  | {type: ExcludeTagsFilterActionTypesEnum.DESELECT_TAG; value: number}
  | {type: ExcludeTagsFilterActionTypesEnum.SET_EXCLUDED_TAGS; values: ReadonlyArray<number>}
  | {type: ExcludeTagsFilterActionTypesEnum.CLEAR}
  | {
      type: ExcludeTagsFilterActionTypesEnum.UPDATE_SELECTED_VALUE;
      value: ExcludeTagFiltersPanelState;
    }
  | {
      type: ExcludeTagsFilterActionTypesEnum.SELECT_WITHOUT_TAGS;
    };

/*
 * Constants.
 */

const {name, iconName, activeIconName} = analyticsFiltersConfig[AnalyticsFilterTypesEnum.EXCLUDE_TAGS];

const tagsFilterDefaultState: State = {
  excludedTags: [],
  withoutTags: false,
};

const modalLabels: FilterButtonSelectionLabels = {
  multiItemLabel: <Trans id="analytics-exclude-tag-filter-button-multi">Is not any of</Trans>,
  singleItemLabel: <Trans id="analytics-exclude-tag-filter-button-single">Is not</Trans>,
};

/*
 * Hook.
 */

export const useAnalyticsExcludeTagsFilter = () => {
  const dispatch = useAppDispatch();
  const tagFiltersPanelState = useAppSelector(getAnalyticsExcludeTagFilters);
  const {trackOnEdit, trackOnRemove} = useFiltersTracking(AnalyticsFilterTypesEnum.EXCLUDE_TAGS);

  const selectedView = useAppSelector(getAnalyticsSelectedView);
  const selectedTeamNamespaces = useAppSelector(getSelectedTeamNamespaces);

  const [state, dispatchAction] = useReducer(tagsFilterReducer, tagsFilterDefaultState);
  const {excludedTags, withoutTags} = state;
  // We store this state separate of our reducer so that
  // changes to it don't cause a re-render of the tags selection dropdown.
  const [tagsMap, setTagsMap] = useState<ResourceMap<TagModel>>({});
  const previousSelection = parsePanelState(tagFiltersPanelState);

  // These refs are needed because otherwise when rendering this dropdown
  // as part of the "More Filters" dropdown the Modal filter state won't
  // update correctly.
  // TODO: refactor so that this isn't needed.
  const filterRowsRef = useRef<ModalFilterItemProps[]>([]);
  const onApplyClickedRef = useRef<() => void>(() => {});

  // This map is needed by the dropdown to render the correct names.
  const tagItems: ResourceMap<TagModel> = {
    ...selectedView?.metadata.fullTags,
    ...tagsMap,
  };

  useEffect(() => {
    // This will set the state based on selectedFilterValue on mount and on update.
    dispatchAction({
      type: ExcludeTagsFilterActionTypesEnum.UPDATE_SELECTED_VALUE,
      value: tagFiltersPanelState,
    });
  }, [tagFiltersPanelState]);

  // This effect is responsible for de-selecting any tags which no longer belong to teams which
  // are currently selected in the teams filter. If no teams are selected, all tags are available.
  useEffect(() => {
    if (!excludedTags) {
      return;
    }

    excludedTags.forEach((selectedTag) => {
      const tag = tagsMap[selectedTag];

      if (
        !tag ||
        selectedTeamNamespaces.size === 0 ||
        selectedTeamNamespaces.has(tag.namespace) ||
        computeIsGlobalNamespace(tag.namespace)
      ) {
        return;
      }

      // Remove the tag from the actual filter state.
      const selectedItemIds = tagFiltersPanelState[AnalyticsFilterTypesEnum.EXCLUDE_TAGS] ?? [];
      const newSelectedItemIds = selectedItemIds.filter((id) => id !== tag.id);
      updateFiltersAction({
        [AnalyticsFilterTypesEnum.EXCLUDE_TAGS]: newSelectedItemIds,
      });

      // De-select this tag as it is no longer available among the current namespaces.
      dispatchAction({type: ExcludeTagsFilterActionTypesEnum.DESELECT_TAG, value: tag.id});
    });
  }, [selectedTeamNamespaces, excludedTags, tagsMap, tagFiltersPanelState]);

  /*
   * Event Handlers.
   */

  // Set the tags for the current selected mode. Does not actually apply the filters, since
  // the modal remains open after setting the tags.
  const onSetTags = (itemIds: ReadonlyArray<number>) => {
    dispatchAction({type: ExcludeTagsFilterActionTypesEnum.SET_EXCLUDED_TAGS, values: itemIds});
  };

  // Apply the current selection.
  onApplyClickedRef.current = () => {
    dispatch(updateFiltersAction(serializeState(state)));
    if (excludedTags) {
      trackOnEdit(excludedTags, previousSelection.excludedTags, {without_tags: false});
    } else {
      trackOnEdit([], previousSelection.excludedTags, {without_tags: true});
    }
  };

  // Cancel the current new selection, return to whatever was applied
  // before the filter was open.
  const onCancelClicked = () => {
    dispatchAction({
      type: ExcludeTagsFilterActionTypesEnum.UPDATE_SELECTED_VALUE,
      value: tagFiltersPanelState,
    });
  };

  // Clear the filters, return to our default (an empty one of selection).
  const onClearClicked = () => {
    dispatchAction({type: ExcludeTagsFilterActionTypesEnum.CLEAR});
    // Reset to our default.
    dispatch(updateFiltersAction(serializeState(tagsFilterDefaultState)));

    trackOnRemove();
  };

  /*
   * Tags selection dropdown.
   */

  const shouldIncludeIcon = isUndefined(excludedTags) || excludedTags.length === 0;

  const renderTagsSelectionButton = () => (
    <AnalyticsFilterButtonValue
      selectedIds={excludedTags}
      itemsMap={tagItems}
      iconName={shouldIncludeIcon ? iconName : undefined}
      label={<Trans id="analytics-exclude-tags-selection-dropdown-inner-label">Select tags</Trans>}
      renderItem={renderTagButtonItem}
      shouldLabelSelection={false}
    />
  );

  const tagsSelectionFilter = () => (
    <TagsSelectionDropdown
      name={name}
      tagsMap={tagsMap}
      setTagsMap={setTagsMap}
      selectedTags={excludedTags ?? []}
      onSetTags={onSetTags}
      onClearAll={() => dispatchAction({type: ExcludeTagsFilterActionTypesEnum.CLEAR})}
      renderButton={renderTagsSelectionButton}
    />
  );

  /*
   * Modal Dropdown.
   */

  const isActive = previousSelection.withoutTags || previousSelection.excludedTags.length > 0;

  const renderButton = () => (
    <AnalyticsFilterButtonValue
      selectedIds={previousSelection.excludedTags}
      itemsMap={tagItems}
      iconName={isActive ? activeIconName : iconName}
      label={computeModalButtonLabel(previousSelection)}
      renderItem={renderTagButtonItem}
      selectionLabels={modalLabels}
      isActive={isActive}
    />
  );

  filterRowsRef.current = [
    {
      value: <Trans id="analytics-tags-filter-modal-doesnt-include">Doesn&apos;t include</Trans>,
      id: 'analytics-doesnt-include-tags',
      isChecked: !withoutTags,
      onSelect: () => {
        dispatchAction({
          type: ExcludeTagsFilterActionTypesEnum.SET_EXCLUDED_TAGS,
          values: excludedTags ?? [],
        });
      },
      renderDropdown: tagsSelectionFilter,
    },
    {
      value: <Trans id="analytics-tags-filter-modal-doesnt-have-any-tags">Doesn&apos;t have any tags</Trans>,
      id: 'analytics-doesnt-have-any-tags',
      isChecked: withoutTags,
      onSelect: () => {
        dispatchAction({
          type: ExcludeTagsFilterActionTypesEnum.SELECT_WITHOUT_TAGS,
        });
      },
    },
  ];

  const renderModalDropdown = (dropdownProps: BaseFilterDropdownProps) => (
    <ModalFilterDropdown
      {...dropdownProps}
      title={name}
      filters={filterRowsRef.current}
      footerHandlers={{onApplyClicked: onApplyClickedRef.current, onCancelClicked}}
      onClear={onClearClicked}
      isActive={isActive}
      shouldCloseOnClear
    />
  );

  return {
    id: 'exclude-tags-filter',
    type: AnalyticsFilterTypesEnum.EXCLUDE_TAGS,
    isActive,
    title: renderFilterTitle(name, iconName),
    renderDropdown: renderModalDropdown,
    renderButton,
  };
};

/*
 * Helpers.
 */

function tagsFilterReducer(state: State, action: Action): State {
  switch (action.type) {
    case ExcludeTagsFilterActionTypesEnum.DESELECT_TAG:
      if (state.withoutTags) {
        return {...state};
      } // no-op

      return {
        ...state,
        excludedTags: without(state.excludedTags, action.value),
      };

    case ExcludeTagsFilterActionTypesEnum.SET_EXCLUDED_TAGS:
      return {
        withoutTags: false,
        excludedTags: action.values,
      };

    case ExcludeTagsFilterActionTypesEnum.SELECT_WITHOUT_TAGS:
      return {
        withoutTags: true,
        excludedTags: undefined,
      };

    case ExcludeTagsFilterActionTypesEnum.CLEAR:
      return tagsFilterDefaultState;

    case ExcludeTagsFilterActionTypesEnum.UPDATE_SELECTED_VALUE:
      return parsePanelState(action.value);

    default:
      return state;
  }
}

function parsePanelState(panelState: ExcludeTagFiltersPanelState): State {
  if (panelState[AnalyticsFilterTypesEnum.WITHOUT_TAGS]) {
    return {excludedTags: undefined, withoutTags: true};
  }

  const stateExcludedTags = panelState[AnalyticsFilterTypesEnum.EXCLUDE_TAGS];
  const excludedTags = stateExcludedTags ?? [];

  return {excludedTags, withoutTags: false};
}

function serializeState(state: State): ExcludeTagFiltersPanelState {
  if (state.withoutTags) {
    return {
      [AnalyticsFilterTypesEnum.EXCLUDE_TAGS]: undefined,
      [AnalyticsFilterTypesEnum.WITHOUT_TAGS]: true,
    };
  }

  const serializedExcludedTags = state.excludedTags.length < 1 ? undefined : state.excludedTags;

  return {
    [AnalyticsFilterTypesEnum.EXCLUDE_TAGS]: serializedExcludedTags,
    [AnalyticsFilterTypesEnum.WITHOUT_TAGS]: false,
  };
}

const computeModalButtonLabel = (previousSelection: State) => {
  if (previousSelection.withoutTags) {
    return <Trans id="analytics-exclude-tags-filter-button-without-label">Doesn&apos;t have tags</Trans>;
  }

  return name;
};
